import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	createOrderReturnRequest,
	getOrderDetailsCombinedRequest,
} from 'redux/reducers/orders/reducer';
import { getConfirmedOrders, getOrderDetailsCombined } from 'redux/reducers/orders/selectors';
import { DialogContent, TextField } from '@mui/material';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { TCreateReturnDialogProps } from './types';
import { CreateReturnItem } from './CreateReturnItem';

export const CreateReturnDialog: FC<TCreateReturnDialogProps> = ({ open, setClose, orderId }) => {
	const dispatch = useDispatch();
	const orderDetailsCombined = useSelector(getOrderDetailsCombined);
	const createReturnItems = orderDetailsCombined?.orderReturnItems;
	const orders = useSelector(getConfirmedOrders);
	const orderDiscounts = orderDetailsCombined?.orderDiscounts;
	const orderReturns = orderDetailsCombined?.orderReturns;

	const [quantityItemsArray, setQuantityItemsArray] = useState<string[]>([]);

	const [createReturnItemsFiltered, setCreateReturnItemsFiltered] = useState(createReturnItems);

	useEffect(() => {
		setQuantityItemsArray(new Array(createReturnItems?.length).fill('0'));

		setCreateReturnItemsFiltered(createReturnItems);
	}, [createReturnItems]);

	const handleClose = () => {
		setClose();
		setCreateReturnItemsFiltered(createReturnItems);
	};

	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const handleConfirmationDialogOpen = () => {
		setConfirmationDialogOpen(true);
	};
	const handleConfirmationDialogClose = () => {
		setConfirmationDialogOpen(false);
	};

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setCreateReturnItemsFiltered(
			createReturnItems?.filter(
				(item) =>
					item.shortDescription.toLowerCase().includes(event.target.value.toLowerCase()) ||
					item.idProvidedByEm.toLowerCase().includes(event.target.value.toLowerCase()),
			),
		);
	};

	useEffect(() => {
		if (!open || !orderId) return;
		dispatch(
			getOrderDetailsCombinedRequest({
				orderId,
			}),
		);
	}, [open, dispatch, orderId]);

	const handleCreateReturn = () => {
		if (!orderId || !createReturnItems) return;

		const returnItemsArray =
			createReturnItems?.map((item, index) => ({
				quantity: +quantityItemsArray[index],
				idProvidedByEm: item.idProvidedByEm,
				remainingQty: item.remainingQty,
				name: 0,
				key: 0,
				isListField: true,
				fieldKey: 0,
				id: item.id,
				emId: item.emId,
				timestampYear: item.timestampYear,
				externalOrderId: item.externalOrderId,
				orderStatus: item.orderStatus,
				shortDescription: item.shortDescription,
				remainingReturnAmount: item.remainingReturnAmount,
				remainingVatAmount: item.remainingVatAmount,
				costProvidedByEm: item.costProvidedByEm,
				proratedDeliveryCost: item.proratedDeliveryCost,
				proratedEasFee: item.proratedEasFee,
				vatValue: item.vatValue,
				itemDeliveryChargeVat: item.itemDeliveryChargeVat,
				proratedEasFeeVat: item.proratedEasFeeVat,
				scheme: item.scheme,
				previousDiscounts: item.previousDiscounts,
			})) ?? [];

		const items = returnItemsArray.filter((item) => +item.quantity > 0);

		dispatch(
			createOrderReturnRequest({
				confirmed: true,
				id: items[0].id,
				orderId,
				returnBreakdown: items,
				returnDate: new Date().toISOString(),
				timestampYear: items[0].timestampYear,
			}),
		);
		handleClose();
		handleConfirmationDialogClose();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				sx={{
					'& .MuiDialog-paper': {
						borderRadius: '12px',
						minWidth: '500px',
					},
				}}
			>
				<DialogTitle color="primary">Creating Order Return</DialogTitle>
				<DialogContent>
					{(createReturnItems?.length ?? 0) > 5 && (
						<TextField
							label="Search"
							variant="outlined"
							fullWidth
							size="small"
							sx={{ mb: 3, mt: 1, '& .MuiInputBase-root': { borderRadius: '12px' } }}
							onChange={handleSearch}
						/>
					)}
					{createReturnItemsFiltered?.map((item, index) => (
						<CreateReturnItem
							key={`${item.id}-${index}`}
							createReturnItem={item}
							returnItemQuantity={orderReturns
								?.flatMap((r) => r?.orderReturnItems ?? [])
								?.filter((d) => d?.idProvidedByEm === item?.idProvidedByEm)
								.reduce((acc, curr) => acc + curr.returnItemQuantity, 0)}
							discount={
								orderDiscounts
									?.find((child) =>
										child.orderDiscountItems.some(
											(element) => element.idProvidedByEm === item.idProvidedByEm,
										),
									)
									?.orderDiscountItems.find(
										(element) => element.idProvidedByEm === item.idProvidedByEm,
									)?.discountPercentage
							}
							value={quantityItemsArray[index] ?? '0'}
							setValue={(value) => {
								const newState = [...quantityItemsArray];
								newState[index] = value;
								setQuantityItemsArray(newState);
							}}
						/>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancel</Button>
					<Button
						onClick={handleConfirmationDialogOpen}
						autoFocus
						disabled={!quantityItemsArray.some((item) => +item > 0)}
					>
						Create Order Return
					</Button>
				</DialogActions>
			</Dialog>
			<ConfirmationDialog
				open={confirmationDialogOpen}
				setClose={handleConfirmationDialogClose}
				handleConfirm={handleCreateReturn}
				title="Order return can`t be changed or deleted.<br />Please confirm saving of the Order return."
			/>
		</>
	);
};
